/**
 * WebSocket keepalive (tick) watchdog.
 *
 * The gateway sends periodic `tick` events. The client tracks when the
 * last tick was received and closes the connection if ticks stop arriving
 * (no tick within 2× the expected interval).
 *
 * This matches the official OpenClaw client behavior:
 * 1. Listen for incoming `tick` events from the server
 * 2. Record the timestamp of the last received tick
 * 3. Close the connection if no tick arrives within 2 × tickIntervalMs
 */

import { DEFAULT_TICK_INTERVAL_MS } from '../utils/constants';

export type OnTickTimeout = () => void;

/**
 * Manages keepalive tick watchdog lifecycle.
 *
 * Start after receiving hello-ok. Stop on disconnect.
 */
export class KeepaliveManager {
  private watchdogId: ReturnType<typeof setInterval> | null = null;
  private lastTickReceived: number = 0;
  private onTimeout: OnTickTimeout;

  constructor(onTimeout: OnTickTimeout) {
    this.onTimeout = onTimeout;
  }

  /** Start the tick watchdog */
  start(intervalMs: number = DEFAULT_TICK_INTERVAL_MS): void {
    this.stop();
    this.lastTickReceived = Date.now();
    this.watchdogId = setInterval(() => {
      if (Date.now() - this.lastTickReceived > intervalMs * 2) {
        this.stop();
        this.onTimeout();
      }
    }, intervalMs);
  }

  /** Handle an incoming tick event from the gateway */
  handleTick(): void {
    this.lastTickReceived = Date.now();
  }

  /** Stop the tick watchdog */
  stop(): void {
    if (this.watchdogId !== null) {
      clearInterval(this.watchdogId);
      this.watchdogId = null;
    }
  }

  /** Whether the watchdog is currently running */
  get isRunning(): boolean {
    return this.watchdogId !== null;
  }
}
